/**
  * @class meleModal
  * @description
  *
  * # Purpose
  * Implements `<mele-modal>` custom web element.
  *
  * # Use
  * Open dialog:
  * ```
  * parentEl.modalOpen({
  *   header: 'A title',
  *   children: ["Message text..."],
  *   footers: [...]
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  * this.invoker is the element that invoked the modal window
  *
  */

import meleView from 'meleView'
const MIN_DIALOG_HEIGHT = 192

const PROPERTIES = _.extend({}, {
  outclick: { type: 'string', description: "what to do when user clicks on dimmed area outside the modal; if set to ':modal.cancel', will close the modal window, if set to 'none' will forward clicks to layers below, otherwise if set, will invoke .publish('mmButtonClicked',{key:this.outclick}) " },
  inclick: { type: 'string', description: "what to do when user clicks on dimmed area outside the modal; if set to ':modal.cancel', will close the modal window, if set to 'none' will forward clicks to layers below, otherwise if set, will invoke .publish('mmButtonClicked',{key:this.outclick}) " },
  position: { type: 'string', description: "position of the modal content; 'over' will be placed to cover calling element, 'below' will be placed just below bottom edge of calling component", enum: ['full', 'center', 'right', 'left', 'top', 'bottom', 'over', 'below', 'above', 'leftto', 'rightto'] },
  width: { type: 'number', description: 'desired width of the element' },
  height: { type: 'number', description: 'desired height of body part' },
  invoker: { type: 'element', description: 'invoking element', noattribute: true },
  eventClose: { type: 'function', description: 'called upon dialog close' },
}, meleView._properties)

export default class meleModal extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this.reposition()
    this.escPressed = (e)=>{
      if(e.keyCode === 27) {
        const body = this.parentElement
        const last_modal = body.querySelector('mele-modal:last-of-type')
        if(last_modal===this) {
          this.eventCancel(this)
        }
      }
    }
    window.addEventListener('keydown', this.escPressed, true)
  } // init

  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    window.removeEventListener('keydown', this.escPressed, true)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * call eventClose before delete
  */
  deleteElement() {
    if(typeof this.eventClose === 'function') {
      this.eventClose(this)
    }
    super.deleteElement()
  } // deleteElement

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Recalculate position using this.invoker element's position.
  * Relevant only for position attribute set to 'over' or 'below'
  */
  reposition() {
    const position = _.toString(this.position)
    let width = parseInt(this.width)||0
    const height = parseInt(this.height)||0

    let realPosition = position
    const def = {
      position: 'absolute',
      top: '',
      bottom: '',
      left: '',
      right: '',
      width: '',
      height: '',
      maxHeight: '',
      borderTopColor: '',
      borderBottomColor: '',
    }
    if(position==='' || position==='full') {
      _.extend(def, {
        top: '0px',
        bottom: '0px',
        left: '0px',
        right: '0px',
      })
    }else if(position==='left' || position==='right') {
      _.extend(def, {
        top: '0px',
        bottom: '0px',
        width: (width>0) ? width+'px' : '',
      })
      def[position] = '0px'
    }else if(position==='top' || position==='bottom') {
      _.extend(def, {
        left: '0px',
        right: '0px',
        height: (height>0) ? height+'px' : '',
        maxHeight: (height>0) ? height+'px' : '',
      })
      def[position] = '0px'
    }else if(position==='center') {
      _.extend(def, {
        position: '',
        height: (height>0) ? height+'px' : '',
        maxHeight: (height>0) ? height+'px' : '',
      })
    }else if(position==='below' || position==='over') {
      // get offset of invoking element from body
      const body = this.ownerDocument.body
      const rect = this.invoker ? this.invoker.getBoundingClientRect() : { top: parseInt(body.offsetHeight/2-32), left: parseInt(body.offsetWidth/4), width: parseInt(body.offsetWidth/2), height: 64 }
      const left = _.max([0, rect.left])
      if(!/^1?\d\d?%$/.test(width)) {
        if(!(width>0)) width = rect.width
        if(left+width>body.offsetWidth) width = body.offsetWidth - left
      }
      _.extend(def, {
        left: left + 'px',
        width: width + 'px',
        height: (height>0) ? height+'px' : '',
      })

      const top = _.max([0, rect.top + (position==='below' ? rect.height : 0)])
      const desired_min_height = height || MIN_DIALOG_HEIGHT
      const available_height = body.offsetHeight - top
      if(available_height>=desired_min_height || top<=parseInt(body.offsetHeight/2)) {
        def.top = top+'px'
        def.maxHeight = _.max([0, available_height-4])+'px'
        def.borderTopColor = 'transparent'
      }else{
        realPosition = 'above'
        const bottom = _.max([0, body.offsetHeight - rect.top - (position==='below' ? 0 : rect.height)])
        def.bottom = bottom+'px'
        const available_height = body.offsetHeight - bottom
        def.maxHeight = _.max([0, available_height-4])+'px'
        def.borderBottomColor = 'transparent'
      }
    }else if(position==='above') {
      // get offset of invoking element from body
      const body = this.ownerDocument.body
      const rect = this.invoker ? this.invoker.getBoundingClientRect() : { top: parseInt(body.offsetHeight/2-32), left: parseInt(body.offsetWidth/4), width: parseInt(body.offsetWidth/2), height: 64 }
      const left = _.max([0, rect.left])
      if(!/^1?\d\d?%$/.test(width)) {
        if(!(width>0)) width = rect.width
        if(left+width>body.offsetWidth) width = body.offsetWidth - left
      }
      _.extend(def, {
        left: left + 'px',
        width: width + 'px',
        height: (height>0) ? height+'px' : '',
      })

      const bottom = _.max([0, body.offsetHeight - rect.top])
      const desired_min_height = height || MIN_DIALOG_HEIGHT
      const available_height = body.offsetHeight - bottom
      if(available_height>=desired_min_height || bottom<=parseInt(body.offsetHeight/2)) {
        def.bottom = bottom+'px'
        def.maxHeight = _.max([0, available_height-4])+'px'
        def.borderBottomColor = 'transparent'
      }else{
        realPosition = 'below'
        const top = _.max([0, rect.top + rect.height])
        def.top = top+'px'
        const available_height = body.offsetHeight - top
        def.maxHeight = _.max([0, available_height-4])+'px'
        def.borderTopColor = 'transparent'
      }
    }else if(position==='leftto') {
      // get offset of invoking element from body
      const body = this.ownerDocument.body
      const rect = this.invoker ? this.invoker.getBoundingClientRect() : { top: parseInt(body.offsetHeight/2-32), left: parseInt(body.offsetWidth/4), width: parseInt(body.offsetWidth/2), height: 64 }
      _.extend(def, {
        top: rect.top + 'px',
        right: (body.offsetWidth-rect.left) + 'px',
      })
    } else if(position==='rightto') {
      // get offset of invoking element from body
      const body = this.ownerDocument.body
      const rect = this.invoker ? this.invoker.getBoundingClientRect() : { top: parseInt(body.offsetHeight/2-32), left: parseInt(body.offsetWidth/4), width: parseInt(body.offsetWidth/2), height: 64 }
      _.extend(def, {
        top: rect.top + 'px',
        left: rect.left + rect.width + 'px',
      })
    }

    this.role = this.behavior==='phone' ? 'narrow' : ''

    this.setAttribute('realposition', realPosition)
    _.extend(this._wrapper.style, def)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "behavior"
  */
  behaviorChanged() {
    this.role = this.behavior==='phone' ? 'narrow' : ''
    super.behaviorChanged()
  } // behaviorChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "width"
  */
  invokerChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "width"
  */
  widthChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "height"
  */
  heightChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "position"
  */
  positionChanged() { this.reposition() }

  eventCancel(self) {
    this.modalClose()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle click outside modal window
  */
  onclick(event) {
    if(event.target===this && this.outclick) {
      if(this.outclick===':modal.cancel') {
        this.eventCancel(this)
      }else {
        this.publish('mmButtonClicked', { holder: this.key, button: this.outclick })
      }
    } else if(this.inclick===':modal.cancel') {
      this.modalClose()
    }
  }

  formModalclose() {
    this.eventCancel(this)
  }
} // class meleModal

meleModal._properties = PROPERTIES
meleModal.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-modal', meleModal)
